import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {ActivationRepository} from '../repositories/ActivationRepository.ts'
import {AuthenticationRepository} from '../repositories/AuthenticationRepository.ts'
import {
    ActivationCommand,
    ActivationsQuery,
    ActivationView,
    AgencyActivationSettings,
    UpdateActivationTravelerPayload,
} from '../domain/entities/Activation.ts'
import {INVITATION_CODES_QUERY_KEY} from './useInvitationCodes.ts'

const activationRepository = new ActivationRepository(new AuthenticationRepository())

export const ACTIVATIONS_QUERY_KEY = 'activations' as const
export const ACTIVATION_QUERY_KEY = 'activation' as const
const ACTIVATION_POLICY_QUERY_KEY = 'activation-policy' as const

// "Listado de no activados en tiempo real": the page the monthly email links
// to, so it refreshes on its own while open.
const NOT_ACTIVATED_POLL_MS = 60_000
const PAGE_SIZE = 200
// Safety stop for a runaway cursor; 20 × 200 activations is far above any
// agency today.
const MAX_PAGES = 20

// Who the request is scoped to. An agency user only ever sees its own
// activations, so it sends no agencyId; staff send ?agencyId= (contract §3).
export type ActivationScope = {agencyId: string; asAdmin: boolean}
const scopedAgencyId = ({agencyId, asAdmin}: ActivationScope) => (asAdmin ? agencyId : undefined)

async function fetchAllActivations(query: ActivationsQuery): Promise<ActivationView[]> {
    const items: ActivationView[] = []
    let cursor: string | undefined
    for (let page = 0; page < MAX_PAGES; page++) {
        const response = await activationRepository.getActivations({...query, limit: PAGE_SIZE, cursor})
        items.push(...(response?.items ?? []))
        if (!response?.nextCursor) break
        cursor = response.nextCursor
    }
    return items
}

// Every activation of the agency, for the pack detail rows (looked up by id).
export function useAgencyActivations(scope: ActivationScope, enabled = true) {
    const query = useQuery({
        queryKey: [ACTIVATIONS_QUERY_KEY, scope.agencyId, 'all'],
        queryFn: () => fetchAllActivations({agencyId: scopedAgencyId(scope)}),
        enabled: !!scope.agencyId && enabled,
    })
    return {
        activations: query.data ?? [],
        isLoadingActivations: query.isLoading,
        activationsError: query.error,
    }
}

// GET /activations?notActivated=true — already sorted by redemptionDeadline asc.
export function useNotActivatedActivations(scope: ActivationScope) {
    const query = useQuery({
        queryKey: [ACTIVATIONS_QUERY_KEY, scope.agencyId, 'not-activated'],
        queryFn: () => fetchAllActivations({agencyId: scopedAgencyId(scope), notActivated: true}),
        enabled: !!scope.agencyId,
        refetchInterval: NOT_ACTIVATED_POLL_MS,
    })
    return {
        activations: query.data ?? [],
        isLoadingActivations: query.isLoading,
        isFetchingActivations: query.isFetching,
        activationsError: query.error,
        updatedAt: query.dataUpdatedAt,
    }
}

// Admin "Contactos automáticos": GET /activations?codeId= then GET /activations/{id}
// for the touchpoints. A generic code (C1) can back several activations.
export function useActivationTouchpointsByCode(codeId: string | null, agencyId?: string) {
    const query = useQuery({
        queryKey: [ACTIVATIONS_QUERY_KEY, 'by-code', codeId, agencyId ?? null],
        queryFn: async () => {
            const page = await activationRepository.getActivations({codeId: codeId!, agencyId})
            const items = page?.items ?? []
            return Promise.all(items.map((a) => activationRepository.getActivation(a.id, agencyId)))
        },
        enabled: !!codeId,
    })
    return {
        activationDetails: query.data ?? [],
        isLoadingActivationDetails: query.isLoading,
        activationDetailsError: query.error,
    }
}

// GET /activations/{id} with touchpoints — the activation a portal deep link
// (?activation={id}, from the agency emails) points at. Refreshes with the list.
export function useActivationDetail(id: string | null, scope: ActivationScope) {
    const query = useQuery({
        queryKey: [ACTIVATION_QUERY_KEY, id, scope.agencyId],
        queryFn: () => activationRepository.getActivation(id!, scopedAgencyId(scope)),
        enabled: !!id && !!scope.agencyId,
        refetchInterval: NOT_ACTIVATED_POLL_MS,
        retry: 1,
    })
    return {
        activationDetail: query.data ?? null,
        isLoadingActivationDetail: query.isLoading,
        activationDetailError: query.error,
    }
}

// Which contact the agency's market makes mandatory (§3.2). Policies change
// rarely; cache them for the session.
export function useActivationPolicy(market: string | undefined, enabled = true) {
    const query = useQuery({
        queryKey: [ACTIVATION_POLICY_QUERY_KEY, market ?? 'DEFAULT'],
        queryFn: () => activationRepository.getActivationPolicy(market || 'DEFAULT'),
        enabled,
        staleTime: Infinity,
        retry: 1,
    })
    return {
        policy: query.data,
        isLoadingPolicy: query.isLoading,
        policyError: query.error,
    }
}

export function useActivationMutations(scope: ActivationScope) {
    const queryClient = useQueryClient()
    const agencyId = scopedAgencyId(scope)
    const invalidate = () => {
        queryClient.invalidateQueries({queryKey: [ACTIVATIONS_QUERY_KEY]})
        queryClient.invalidateQueries({queryKey: [ACTIVATION_QUERY_KEY]})
        queryClient.invalidateQueries({queryKey: [INVITATION_CODES_QUERY_KEY, scope.agencyId]})
        queryClient.invalidateQueries({queryKey: ['b2b-pack-detail', scope.agencyId]})
    }

    const travelerMutation = useMutation({
        mutationFn: ({id, payload}: {id: string; payload: UpdateActivationTravelerPayload}) =>
            activationRepository.updateActivationTraveler(id, payload, agencyId),
        onSuccess: invalidate,
    })

    const commandMutation = useMutation({
        mutationFn: ({id, command}: {id: string; command: ActivationCommand}) =>
            activationRepository.sendActivationCommand(id, command, agencyId),
        onSuccess: invalidate,
    })

    return {
        updateActivationTraveler: travelerMutation.mutateAsync,
        isUpdatingActivationTraveler: travelerMutation.isPending,
        sendActivationCommand: commandMutation.mutateAsync,
        isSendingActivationCommand: commandMutation.isPending,
    }
}

// Agency self-service: PATCH /agencies/me/activation-settings.
export function useUpdateMyActivationSettings() {
    const queryClient = useQueryClient()
    const mutation = useMutation({
        mutationFn: (settings: AgencyActivationSettings) => activationRepository.updateMyActivationSettings(settings),
        onSuccess: () => {
            // Changing optInPreTrip replans every open activation (§2.2).
            queryClient.invalidateQueries({queryKey: [ACTIVATIONS_QUERY_KEY]})
        },
    })
    return {
        updateMyActivationSettings: mutation.mutateAsync,
        isUpdatingMyActivationSettings: mutation.isPending,
    }
}

// Call after any assignment that carried an `activation` block.
export function useInvalidateActivations() {
    const queryClient = useQueryClient()
    return () => {
        queryClient.invalidateQueries({queryKey: [ACTIVATIONS_QUERY_KEY]})
        queryClient.invalidateQueries({queryKey: [ACTIVATION_QUERY_KEY]})
    }
}
